import { animate, style, transition, trigger } from '@angular/animations';
import { Component, HostListener, Input, OnInit } from '@angular/core';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInput } from '@angular/material/input';
import { MatCardModule } from '@angular/material/card';
import { MatIconModule } from '@angular/material/icon';
import { MatBadgeModule } from '@angular/material/badge';
import { SharedModule } from '@App/shared/shared.module';
import { HubConnection } from '@microsoft/signalr';
import { TeamsNotification, Notification } from '@Models';
import { SignalRService, TeamsService } from '@Services';
import { finalize } from 'rxjs';

@Component({
    selector: 'app-notifications',
    templateUrl: './notifications.component.html',
    styleUrls: ['./notifications.component.scss'],
    imports: [MatFormFieldModule, MatInput, MatCardModule, MatIconModule, MatBadgeModule, SharedModule],
    animations: [
        trigger('fadeIn', [
            transition(':enter', [
                style({ opacity: 0, transform: 'translateY(-5px)' }),
                animate('150ms ease-out', style({ opacity: 1, transform: 'translateY(0)' })),
            ]),
            transition(':leave', [
                animate('100ms ease-in', style({ opacity: 0, transform: 'translateY(-5px)' })),
            ])
        ])
    ]
})
export class NotificationsComponent implements OnInit {
    @Input() userId: string;

    filterValue = '';
    hideNotificationsBadge = false;
    signalrConnection: HubConnection;
    isNotificationsDropdownOpen = false;
    notifications: Notification[] = [];
    filteredNotifications: Notification[] = [];

    constructor(
        public signalrService: SignalRService,
        public teamsService: TeamsService
    ) 
    {

    }

    ngOnInit() {
        this.loadNotifications(this.userId);
        this.setupSignalR();
    }

    loadNotifications(userId: string) {
        this.teamsService.getUserNotifications(userId, true).subscribe((notifications: Notification[]) => {
            this.notifications = notifications;

            this.applyFilterNotifications();
        });
    }

    getUnreadNotificationsCount() {
        return this.notifications.filter(n => n.state != 'READ').length;
    }

    markAllNotificationsAsRead() {
        const unreadNotificationIds = this.notifications
            .filter(n => n.state === 'UNREAD')
            .map(n => n.notificationId);

        if(unreadNotificationIds.length === 0) return;

        this.teamsService.markAllNotificationsAsRead(this.userId, true)
            .pipe(
                finalize(() => {
                    this.hideNotificationsBadge = false;
                })
            )
            .subscribe({
                next: () => {
                    for (const notification of this.notifications) {
                        notification.state = 'READ';
                    }
                },
                error: (err) => console.error(err)
            });
    }

    toggleDropdown() {
        this.isNotificationsDropdownOpen = !this.isNotificationsDropdownOpen;

        if(this.isNotificationsDropdownOpen) {
            this.filterValue = '';
            this.applyFilterNotifications();
            this.hideNotificationsBadge = true;
        } else {
            this.markAllNotificationsAsRead();
        }
    }

    applyFilterNotifications() {
        if (!this.filterValue) {
            this.filteredNotifications = [...this.notifications];
            return;
        }

        this.filteredNotifications = this.notifications.filter(n => {
            return n.eventType.toLowerCase().includes(this.filterValue.toLowerCase()) ||
                n.message.toLowerCase().includes(this.filterValue.toLowerCase());
        });
    }

    updateFilteredNotifications(searchText: string) {
        this.filterValue = searchText;
        this.applyFilterNotifications();
    }

    @HostListener('document:click', ['$event'])
    onClickOutside(event: Event) {
        const target = event.target as HTMLElement;
        if (!this.isNotificationsDropdownOpen || target.closest('.notification-container'))
            return;

        // Only do certain things if a. the dropdown is open and b. the click was outside the notification component elements
        this.isNotificationsDropdownOpen = false;
        this.markAllNotificationsAsRead();
    }

    //#region SignalR
    setupSignalR() {
        // Create HubConnection

        this.signalrConnection = this.signalrService.getGlobalSignalRConnection();
        this.signalrConnection.serverTimeoutInMilliseconds = 1000 * 60 * 2; // 2 minutes

        // Setup Listeners

        this.signalrConnection.on('connected', function (event) {
            console.log('SignalR Connected for global : ' + event);
        });

        this.signalrConnection.on('teamsNotification', (payload: TeamsNotification) => {
            if (payload.teamAdminIds.includes(this.userId)) {
                this.loadNotifications(this.userId);
            }
        });

        this.signalrConnection.onclose(this.signalrOnClose.bind(this));

        // Start SignalR
        this.signalrConnection.start().then(this.signalrStarted.bind(this)).catch(this.signalrStartError);
    }

    signalrStarted() {
        console.log('Notifications Component | SignalR connection started');
    }

    signalrStartError(error: any) {
        console.error(error.message);
    }

    signalrOnClose(error: any) {
        console.log('SignalR Disconnected ');
    }
    //#endregion
}
